import { useCallback, useEffect, useMemo, useState } from 'react'
import { TileSpecification } from 'src/queries/agent/network/agent.models'

import {
  buildPinnedInsightKey,
  loadPinnedInsights,
  MAX_PINNED_INSIGHTS,
  persistPinnedInsights,
  PinnedInsightStored,
} from './pinnedInsights.storage'

export type PinResult = 'success' | 'limit' | 'duplicate' | 'error'

export interface UsePinnedInsightsResult {
  pinnedInsights: PinnedInsightStored[]
  isPinned: (spec: TileSpecification) => boolean
  pin: (spec: TileSpecification) => PinResult
  unpin: (specOrKey: TileSpecification | string) => boolean
  pinLimitReached: boolean
}

const usePinnedInsights = (userId?: string | null): UsePinnedInsightsResult => {
  const [pinnedInsights, setPinnedInsights] = useState<PinnedInsightStored[]>(
    [],
  )

  useEffect(() => {
    setPinnedInsights(loadPinnedInsights(userId))
  }, [userId])

  const keyLookup = useMemo(() => {
    return new Set(pinnedInsights.map((item) => item.key))
  }, [pinnedInsights])

  const pin = useCallback(
    (spec: TileSpecification): PinResult => {
      const key = buildPinnedInsightKey(spec)

      let outcome: PinResult = 'error'

      setPinnedInsights((current) => {
        if (current.some((item) => item.key === key)) {
          outcome = 'duplicate'
          return current
        }

        if (current.length >= MAX_PINNED_INSIGHTS) {
          outcome = 'limit'
          return current
        }

        const next: PinnedInsightStored[] = [
          {
            key,
            spec,
            title: spec.title,
            pinnedAt: new Date().toISOString(),
          },
          ...current,
        ]

        outcome = persistPinnedInsights(userId, next) ? 'success' : 'error'

        return next
      })

      return outcome
    },
    [userId],
  )

  const unpin = useCallback(
    (specOrKey: TileSpecification | string): boolean => {
      const key =
        typeof specOrKey === 'string'
          ? specOrKey
          : buildPinnedInsightKey(specOrKey)

      let removed = false

      setPinnedInsights((current) => {
        const next = current.filter((item) => {
          const match = item.key === key
          if (match) {
            removed = true
          }
          return !match
        })

        if (removed) {
          persistPinnedInsights(userId, next)
        }

        return next
      })

      return removed
    },
    [userId],
  )

  const isPinned = useCallback(
    (spec: TileSpecification): boolean => {
      const key = buildPinnedInsightKey(spec)
      return keyLookup.has(key)
    },
    [keyLookup],
  )

  return {
    pinnedInsights,
    isPinned,
    pin,
    unpin,
    pinLimitReached: pinnedInsights.length >= MAX_PINNED_INSIGHTS,
  }
}

export default usePinnedInsights
